import {
  AiOutlineAudit,
  AiOutlineCreditCard,
  AiOutlineHistory,
  AiOutlineHome,
  AiOutlineSearch,
  AiOutlineSetting,
  AiOutlineTeam,
  AiOutlineTransaction,
  AiOutlineWarning,
} from "react-icons/ai";
import { MdOutlineBusiness } from "react-icons/md";
import { Link } from "react-router";

import { featureFlags } from "shell/platform/config/featureFlags";
import { pathsSchema } from "shell/platform/config/paths";
import {
  MONEY_MOVEMENT_ROLES,
  SPONSOR_GUARD_ROLES,
} from "shell/platform/constants/constants";

import type { MenuDeps, MenuItem } from "../types";

import { compactMenuItems } from "./compactMenuItems";

const { isProd, enableSponsorGuard } = featureFlags;

const buildSystemAdminChildren = ({
  t,
  isInternal,
  hasWebhookAccess,
  hasRole,
}: MenuDeps): MenuItem[] =>
  compactMenuItems([
    hasRole(["Organisation Admin", "Internal", "Internal Read Only"]) && {
      key: "organization",
      label: t("clientAccess:components.mainMenu.organization"),
      children: compactMenuItems([
        {
          key: "organization-dashboard",
          label: (
            <Link to={pathsSchema.portal.organization.index}>
              {t("clientAccess:components.mainMenu.dashboard")}
            </Link>
          ),
        },
        featureFlags.enableApplications && {
          key: "applications",
          label: (
            <Link to={pathsSchema.portal.organization.applications.index}>
              {t("common:components.mainMenu.applications")}
            </Link>
          ),
        },
        featureFlags.enableUserManagement && {
          key: "users-dashboard",
          label: (
            <Link to={pathsSchema.portal.organization.users.index}>
              {t("common:components.mainMenu.userManagement")}
            </Link>
          ),
        },
        {
          key: "ehi-configuration",
          label: (
            <Link to={pathsSchema.organizations.ehiConfiguration.index}>
              {t("common:components.mainMenu.ehiConfiguration")}
            </Link>
          ),
        },
      ]),
    },
    isInternal && {
      key: "user-admin",
      label: <Link to={pathsSchema.auditLog.index}>Audit Logs</Link>,
    },
    hasWebhookAccess && {
      key: "webhooks",
      label: <Link to={pathsSchema.portal.webhooks}>Webhooks</Link>,
    },
  ]);

export const buildPortalMenuItems = (deps: MenuDeps): MenuItem[] => {
  const { hasPanFinderAccess, isInternal, colorPrimary, t, hasRole } = deps;
  const hasSponsorGuardAccess =
    enableSponsorGuard && hasRole(SPONSOR_GUARD_ROLES);
  const hasMoneyMovementAccess = hasRole(MONEY_MOVEMENT_ROLES);
  const adminChildren = buildSystemAdminChildren(deps);

  return compactMenuItems([
    {
      key: "home",
      label: (
        <Link
          to={
            hasSponsorGuardAccess
              ? pathsSchema.portal.sponsorGuard.index
              : pathsSchema.portal.index
          }
        >
          Home
        </Link>
      ),
      icon: <AiOutlineHome color={colorPrimary} />,
    },
    {
      key: "cards-transactions",
      label: (
        <Link
          to={pathsSchema.portal.search}
          data-e2e="cardTransactionsMenuItem"
        >
          Cards & Transactions
        </Link>
      ),
      icon: <AiOutlineCreditCard color={colorPrimary} />,
    },
    !isProd &&
      hasMoneyMovementAccess && {
        key: "ach",
        label: (
          <Link to={pathsSchema.portal.moneyMovement}>
            {t("portal:components.ach.moneyMovement.title")}
          </Link>
        ),
        icon: <AiOutlineTransaction color={colorPrimary} />,
      },
    hasPanFinderAccess && {
      key: "pan-finder",
      label: <Link to={pathsSchema.portal.panfinder}>PAN Finder</Link>,
      icon: <AiOutlineSearch color={colorPrimary} />,
    },
    hasSponsorGuardAccess && {
      key: "sponsor-guard-clients",
      label: (
        <Link to={pathsSchema.portal.sponsorGuard.clients}>
          {t("portal:components.sponsorGuard.clients.title")}
        </Link>
      ),
      icon: <MdOutlineBusiness color={colorPrimary} />,
    },
    hasSponsorGuardAccess && {
      key: "sponsor-guard-approvals",
      label: (
        <Link to={pathsSchema.portal.sponsorGuard.approvals}>
          {t("portal:components.sponsorGuard.approvals.title")}
        </Link>
      ),
      icon: <AiOutlineAudit color={colorPrimary} />,
    },
    hasSponsorGuardAccess && {
      key: "sponsor-guard-audit-logs",
      label: (
        <Link to={pathsSchema.portal.sponsorGuard.auditLogs}>
          {t("portal:components.sponsorGuard.auditLogs.title")}
        </Link>
      ),
      icon: <AiOutlineHistory color={colorPrimary} />,
    },
    hasSponsorGuardAccess && {
      key: "sponsor-guard-configuration",
      label: (
        <Link to={pathsSchema.portal.sponsorGuard.configuration}>
          {t("common:labels.configuration")}
        </Link>
      ),
      icon: <AiOutlineSetting color={colorPrimary} />,
    },
    adminChildren.length > 0 && {
      key: "sys-admin",
      label: t("common:components.mainMenu.systemAdmin"),
      icon: <AiOutlineTeam color={colorPrimary} />,
      children: adminChildren,
    },
    featureFlags.enableDisputes &&
      isInternal && {
        key: "abo",
        label: <Link to={pathsSchema.portal.abo}>ABO</Link>,
        icon: <AiOutlineWarning color={colorPrimary} />,
      },
    featureFlags.enableDisputes &&
      isInternal && {
        key: "disputes-and-chargeback",
        label: (
          <Link to={pathsSchema.portal.disputesAndChargeback}>
            {t("portal:components.disputesAndChargeback.title")}
          </Link>
        ),
        icon: <AiOutlineWarning color={colorPrimary} />,
      },
  ]);
};
